import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n, { applyDocumentLanguage } from '@/i18n';
import LanguagePickerButton from '@/components/LanguagePickerButton';
import { ChevronRight, Volume2 } from 'lucide-react';
import { INTRO_VIDEOS, introVideoFor, introVideoUrl } from '@/lib/introVideos';

/**
 * Full-screen intro video for logged-out users, replacing the old brand
 * splash and the onboarding slides. When there is more than one video,
 * language buttons switch both the video and the app's language. Skip or Continue go to login; so
 * does the end of the video.
 *
 * Browsers only allow sound after the user has tapped something, so the
 * video starts with sound when it can, otherwise muted with a "tap for
 * sound" button. A language tap counts, so switching language plays the
 * new video with sound. If the file can't load (not uploaded yet, no
 * network) the page falls back to the brand screen with Continue.
 */
export default function IntroVideo() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const [video, setVideo] = useState(() => introVideoFor(i18n.language));
  const [muted, setMuted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [progress, setProgress] = useState(0);

  const goToLogin = useCallback(() => navigate('/login', { replace: true }), [navigate]);

  // Start the current video: with sound if the browser allows it,
  // otherwise muted (and offer a button to turn the sound on).
  const start = useCallback(async () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = false;
    try {
      await el.play();
      setMuted(false);
    } catch {
      el.muted = true;
      setMuted(true);
      el.play().catch(() => { /* onError shows the fallback */ });
    }
  }, []);

  useEffect(() => {
    setFailed(false);
    setProgress(0);
    start();
  }, [video, start]);

  const chooseLanguage = async (next) => {
    if (next.lang !== i18n.language) {
      await i18n.changeLanguage(next.lang);
      applyDocumentLanguage(next.lang);
    }
    if (next.lang === video.lang) {
      if (videoRef.current) videoRef.current.currentTime = 0;
      start();
    } else {
      setVideo(next);
    }
  };

  const unmute = () => {
    const el = videoRef.current;
    if (!el) return;
    el.muted = false;
    setMuted(false);
    el.play().catch(() => {});
  };

  return (
    <div className="fixed inset-0 bg-black flex flex-col" data-testid="intro-video-page">
      {/* Top bar: language choice + skip */}
      <div className="relative z-10 flex items-center justify-between gap-2 px-4 pt-4">
        <div className="flex gap-2" role="group" aria-label="Language">
          {INTRO_VIDEOS.length > 1 && INTRO_VIDEOS.map((v) => (
            <button
              type="button"
              key={v.lang}
              onClick={() => chooseLanguage(v)}
              aria-pressed={v.lang === video.lang}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                v.lang === video.lang
                  ? 'bg-white text-farm-primary border-white'
                  : 'bg-white/10 text-white border-white/40 hover:bg-white/20'
              }`}
              data-testid={`intro-lang-${v.lang}`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={goToLogin}
          className="px-4 py-2 rounded-full text-sm font-semibold text-white bg-white/10 border border-white/40 hover:bg-white/20"
          data-testid="intro-skip"
        >
          {t('intro.skip')}
        </button>
      </div>

      {/* Video, or the brand screen when it can't play */}
      <div className="relative flex-1 flex items-center justify-center overflow-hidden">
        {failed ? (
          <div className="text-center px-6" data-testid="intro-fallback">
            <span className="text-8xl mb-4 block">🌾</span>
            <h1 className="text-3xl font-bold text-white mb-2">{t('splash.brand')}</h1>
            <p className="text-white/90 text-lg">{t('splash.tagline')}</p>
          </div>
        ) : (
          <video
            ref={videoRef}
            key={video.lang}
            src={introVideoUrl(video)}
            className="w-full h-full object-contain"
            playsInline
            preload="auto"
            onEnded={goToLogin}
            onError={() => setFailed(true)}
            onTimeUpdate={(e) => {
              const { currentTime, duration } = e.currentTarget;
              if (duration) setProgress(currentTime / duration);
            }}
            data-testid="intro-video"
          />
        )}

        {muted && !failed && (
          <button
            type="button"
            onClick={unmute}
            className="absolute bottom-6 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-farm-primary font-semibold shadow-lg"
            data-testid="intro-unmute"
          >
            <Volume2 className="w-5 h-5" /> {t('intro.tapForSound')}
          </button>
        )}
      </div>

      {/* Progress, language (very big, beta feedback), continue */}
      <div className="relative z-10 px-4 pb-6 pt-3">
        {!failed && (
          <div className="h-1 w-full bg-white/20 rounded-full mb-4 overflow-hidden">
            <div className="h-full bg-white transition-[width] duration-300" style={{ width: `${progress * 100}%` }} />
          </div>
        )}
        <div className="mb-3">
          <LanguagePickerButton size="xl" />
        </div>
        <button
          type="button"
          onClick={goToLogin}
          className="w-full h-14 rounded-xl bg-farm-primary text-white font-semibold text-lg inline-flex items-center justify-center"
          data-testid="intro-continue"
        >
          {t('languageSelect.continue')} <ChevronRight className="w-5 h-5 ml-1" />
        </button>
      </div>
    </div>
  );
}
